import { useMemo } from 'react'

import {
  CommandActionArgs,
  CommandContextArgs,
  CommandMenuContextType,
  type CommandMenuViewType,
} from '~/components/command-menu/types'
import { useAuth } from '~/context/AuthProvider'
import { useOrgCompliance } from '~/hooks/compliance'
import {
  useActiveOrg,
  useActiveOrgId,
  useIsOrgOverridden,
  useResetActiveOrg,
} from '~/hooks/org'
import { useActiveOrgMember } from '~/hooks/org/useActiveOrgMember'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useCopyValue } from '~/hooks/useCopyValue'
import { useDevtoolsVisible } from '~/hooks/useDevtoolsVisible'
import { useLogout } from '~/hooks/useLogout'
import { useNavigation } from '~/hooks/useNavigation'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { IAddress } from '~/types'

export const useCommandArgsValue = ({
  activeView,
  pathname,
  address,
  setActiveView,
}: {
  activeView: CommandMenuViewType | null
  pathname: string
  address: IAddress | undefined
  setActiveView: CommandMenuContextType['setActiveView']
}) => {
  const isCommandMenuOpen = activeView !== null
  const { user } = useAuthUser()
  const { setGlobalModalState } = useAuth()
  const navigate = useNavigation()
  const orgMember = useActiveOrgMember()
  const { org } = useActiveOrg()
  const { smartAccounts } = useSmartAccounts()
  const { handleCopy } = useCopyValue()
  const { isVerified, canAddRecipients } = useOrgCompliance({
    enabled: isCommandMenuOpen,
  })
  const logout = useLogout()
  const isOrgOverridden = useIsOrgOverridden()
  const activeOrgId = useActiveOrgId()
  const resetActiveOrg = useResetActiveOrg()
  const devtoolsVisible = useDevtoolsVisible()

  const contextArgs = useMemo<CommandContextArgs>(
    () => ({
      activeView,
      pathname,
      orgMember,
      address,
      smartAccount: address
        ? smartAccounts.find((smartAccount) => smartAccount.address === address)
        : undefined,
      compliance: {
        isVerified: isVerified ?? false,
        canAddRecipients: canAddRecipients ?? false,
      },
      hasLinkedBankAccount: !!org?.bridge?.externalAccounts?.length,
      isSplitsAdmin: user?.isSplitsAdmin,
      isOrgOverridden,
      activeOrgId,
      devtoolsVisible,
    }),
    [
      activeView,
      pathname,
      address,
      orgMember,
      smartAccounts,
      isVerified,
      canAddRecipients,
      org?.bridge?.externalAccounts,
      user?.isSplitsAdmin,
      isOrgOverridden,
      activeOrgId,
      devtoolsVisible,
    ],
  )

  const actionArgs = useMemo<CommandActionArgs>(
    () => ({
      navigate,
      setGlobalModalState,
      setActiveView,
      copyToClipboard: handleCopy,
      logout,
      resetActiveOrg,
    }),
    [
      navigate,
      setGlobalModalState,
      setActiveView,
      handleCopy,
      logout,
      resetActiveOrg,
    ],
  )

  return useMemo(() => ({ contextArgs, actionArgs }), [contextArgs, actionArgs])
}
